import type { TranslateService } from '@ngx-translate/core';

const LANGUAGE_STORAGE_KEY = 'wasel.admin.lang';

export type SupportedLanguage = 'en-US' | 'ar-SA';

/** Wasel supports Arabic and English only; anything else (including retired fr/es) is English. */
export const normalizeStoredLanguage = (lang: string): SupportedLanguage =>
  lang.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US';

export const readStoredLanguage = (): SupportedLanguage | undefined => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored ? normalizeStoredLanguage(stored) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The `Accept-Language` for an API request: the stored choice, else the active ngx-translate language, normalised to
 * exactly `en-US` / `ar-SA` (the backend's supported cultures; a bare `en` would fall back to its default, Arabic).
 */
export const requestLanguage = (translate: Pick<TranslateService, 'getCurrentLang' | 'getFallbackLang'>): SupportedLanguage =>
  normalizeStoredLanguage(readStoredLanguage() ?? (translate.getCurrentLang() || translate.getFallbackLang() || 'en-US'));

export const writeStoredLanguage = (lang: string): void => {
  try {
    const normalized = normalizeStoredLanguage(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage failures.
  }
};
